import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Leftsidebar from "../../sidebar/leftsidebar";
import LeftResponsivesidebar from "../../sidebar/left-responsive";
import Headmenu from "../../sidebar/headmenu";
import BillingBackButton from "./BillingBackButton";
import { getInvoiceDetail, payInvoice } from "../../../hook/useAdsApi";

// Same split-Stripe-elements pattern as AdAddCredits.js/AdPaymentMethods.js - kept consistent
// rather than extracted into a shared component, per this codebase's flat-file-first convention.
const ELEMENT_OPTIONS = {
  style: {
    base: { fontSize: "14px", color: "#374151", "::placeholder": { color: "#9ca3af" }, fontFamily: "inherit" },
    invalid: { color: "#BE1F2E" },
  },
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// Reused from both the Dashboard's "Pay Now" banner and the Invoices list/detail modal - the
// postpaid stakeholder decision means this is the only place a postpaid charge actually happens,
// always with a fresh payment_method_id (no saved/auto-charged card).
export default function AdPayInvoice() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [cardholderName, setCardholderName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    getInvoiceDetail(invoiceId)
      .then((res) => setInvoice(res))
      .catch(() => setInvoice(null))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  const handlePay = async () => {
    if (!stripe || !elements) {
      toast.error("Payment form is still loading, try again in a moment");
      return;
    }
    if (!cardholderName.trim()) {
      toast.error("Enter the cardholder name");
      return;
    }

    setSubmitting(true);
    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: { name: cardholderName },
      });
      if (stripeError) {
        toast.error(stripeError.message);
        return;
      }

      await payInvoice(invoiceId, { payment_method_id: paymentMethod.id });
      setPaid(true);
      toast.success("Invoice paid successfully");
    } catch (err) {
      toast.error(err.response?.data?.message || "Payment failed, try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard w-full min-h-screen flex">
        <Leftsidebar />
        <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
          <LeftResponsivesidebar />
          <Headmenu />
          <p className="text-sm text-gray-500">Loading invoice...</p>
        </main>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="dashboard w-full min-h-screen flex">
        <Leftsidebar />
        <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
          <LeftResponsivesidebar />
          <Headmenu />
          <p className="text-sm text-gray-500">Invoice not found.</p>
        </main>
      </div>
    );
  }

  const alreadyPaid = paid || invoice.payment_status === "paid";

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Pay Invoice</h1>
                <p className="text-sm text-gray-500 mt-1">Invoice #{invoice.invoice_number}</p>
                <div className="border-b border-gray-200 mt-2 w-full max-w-[420px]" />
              </div>
              <BillingBackButton />
            </div>

            <div className="rounded-2xl border border-gray-200 p-5 mb-5">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">Billing Period</span>
                <span className="font-semibold">
                  {formatDate(invoice.billing_period_start)} - {formatDate(invoice.billing_period_end)}
                </span>
              </div>
              {invoice.due_date && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Due Date</span>
                  <span className="font-semibold">{formatDate(invoice.due_date)}</span>
                </div>
              )}
              <div className="flex justify-between items-center bg-[#ecfbf3] rounded-xl px-4 py-3 mt-3">
                <span className="font-semibold text-sm">Amount Due</span>
                <span className="font-black text-lg">
                  {invoice.currency_code} {Number(invoice.total_amount).toFixed(2)}
                </span>
              </div>
            </div>

            {alreadyPaid ? (
              <div className="rounded-2xl border border-[#39B54A] bg-[#ecfbf3] p-5 text-center">
                <p className="font-bold text-[#095228]">This invoice is paid.</p>
                <button
                  type="button"
                  onClick={() => navigate("/ads/billing/invoices")}
                  className="mt-3 bg-[#095228] text-white font-semibold px-6 py-2 rounded-full text-sm"
                >
                  Back to Invoices
                </button>
              </div>
            ) : (
              <div className="rounded-2xl bg-gray-50 p-5">
                <h3 className="font-semibold text-sm mb-3">Card Details</h3>
                <label className="block text-sm text-gray-600 mb-1">Card number</label>
                <div className="border border-gray-300 rounded-lg px-3 py-2.5 mb-3 bg-white">
                  <CardNumberElement options={ELEMENT_OPTIONS} />
                </div>
                <label className="block text-sm text-gray-600 mb-1">Cardholder name</label>
                <input
                  type="text"
                  placeholder="Name"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
                />
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Expiry date</label>
                    <div className="border border-gray-300 rounded-lg px-3 py-2.5 bg-white">
                      <CardExpiryElement options={ELEMENT_OPTIONS} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">CVV / CVC</label>
                    <div className="border border-gray-300 rounded-lg px-3 py-2.5 bg-white">
                      <CardCvcElement options={ELEMENT_OPTIONS} />
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePay}
                  disabled={submitting}
                  className="w-full bg-[#095228] text-white font-semibold py-2.5 rounded-full text-sm hover:opacity-90 transition disabled:opacity-60"
                >
                  {submitting ? "Processing..." : `Pay ${invoice.currency_code} ${Number(invoice.total_amount).toFixed(2)}`}
                </button>
              </div>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
